import { Spacing } from '@persona-id/design-tokens';
import { FC, PropsWithChildren } from 'react';
import styled from 'styled-components';

import Table, { TableCell, TableHead, TableRow } from 'design-system/components/Table';
import { isHovered } from 'design-system/lib/pseudoClass';
import { TransitionDuration } from 'design-system/variables/transitions';

export const NEST_SPACING = 32;
// temp arbitrary max depth
const MAXIMUM_DEPTH = 6;

const CollapsibleTableRow = styled(TableRow)<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'table-row' : 'none')};
  transition-duration: ${TransitionDuration.Fast};
  &${isHovered} {
    background-color: inherit;
  }
`;

const NestedTableCell = styled(TableCell)<{ showBottomBorder: boolean }>`
  position: relative;
  height: 1px;
  padding: 0;
  border: 0;
  ${({ showBottomBorder, theme }) =>
    showBottomBorder ? `border-bottom: 1px solid ${theme.color.border.primary};` : ''}
`;

const NestIndicatorContainer = styled.div<{ nestLevel: number; maxDepth?: number }>`
  display: flex;
  position: absolute;
  height: 100%;
  padding-bottom: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.primary};
  z-index: ${({ nestLevel, maxDepth }) => maxDepth ?? MAXIMUM_DEPTH - nestLevel};
`;

const IndicatorSpacer = styled.div.attrs(() => ({ 'aria-hidden': true }))<{ nestLevel: number }>`
  width: ${({ nestLevel }) => NEST_SPACING * nestLevel + 18.5}px;
`;

const NestIndicator = styled.span`
  position: relative;
  width: 15px;
  height: 100%;
  vertical-align: top;
  border-bottom-left-radius: 10px;
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  top: -8px;
`;

const NestedTable = styled(Table)`
  display: inline-table;
  table-layout: fixed;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
`;

interface ExpandableFieldRowProps {
  colSpan: number;
  nestLevel: number;
  maxDepth?: number;
  indicatorSpacer?: React.ReactNode;
  visible: boolean;
  showBottomBorder: boolean;
}

const ExpandableFieldRow: FC<PropsWithChildren<ExpandableFieldRowProps>> = ({
  colSpan,
  nestLevel,
  maxDepth,
  indicatorSpacer,
  visible,
  children,
  showBottomBorder,
}) => {
  if (children == null) return <></>;
  return (
    <CollapsibleTableRow visible={visible}>
      <NestedTableCell colSpan={colSpan} showBottomBorder={showBottomBorder}>
        <NestIndicatorContainer nestLevel={nestLevel} maxDepth={maxDepth}>
          {indicatorSpacer ?? <IndicatorSpacer nestLevel={nestLevel} />}
          <NestIndicator />
        </NestIndicatorContainer>
        <NestedTable>
          <TableHead />
          {children}
        </NestedTable>
      </NestedTableCell>
    </CollapsibleTableRow>
  );
};

export default ExpandableFieldRow;
